import { FC, useEffect, useMemo, useRef, useState } from "react";

import { useApiCall } from "@framework/react-hooks";
import type { ISpeedDataMarkResponse } from "@framework/types";

import { useSpeed } from "../../../../../../context/speed-context";
import type { IMultiAnalysisDataMark } from "../../../multi-analysis/types";
import { normalizeTrendRangeForRequest } from "../../../multi-analysis/utils/trend-series";
import type { ISpeedScreenFormTimePanelProps } from "../types";
import { isDateInRange } from "../../../multi-analysis/utils/data-marks";
import {
  collectOrbitQueryPoints,
  expandTimestampRange,
  isOrbitPairMark,
  ORBIT_TIME_TOLERANCE_MS,
  pairOrbitSignalMarks,
  SIGNAL_WAVEFORM_MEASURE_TYPE,
} from "./pair-marks";

const EMPTY_ROWS: ISpeedDataMarkResponse[] = [];

export const OrbitTimePanel: FC<ISpeedScreenFormTimePanelProps> = ({
  trainId,
  globalRange,
  selectedParameter,
  controlUnits,
  trendSeries,
  onLoadingChange,
}) => {
  const { selectedPoints, pointsByTrainId, loadTrainPoints, setDataMarks, setDataMarksError } = useSpeed();
  const allPoints = pointsByTrainId[trainId] ?? [];
  const queryPoints = useMemo(
    () => collectOrbitQueryPoints(selectedPoints, allPoints.length ? allPoints : selectedPoints),
    [allPoints, selectedPoints],
  );
  const pointIds = useMemo(
    () => JSON.stringify(queryPoints.map(point => point.id).sort((left, right) => left - right)),
    [queryPoints],
  );
  const { start, end } = globalRange;
  const range = useMemo(() => normalizeTrendRangeForRequest({ start, end }), [start, end]);
  const queryRange = useMemo(() => (range ? expandTimestampRange(range, ORBIT_TIME_TOLERANCE_MS) : null), [range]);
  const requestKey = queryPoints.length && queryRange ? JSON.stringify([trainId, pointIds, queryRange]) : null;
  const [result, setResult] = useState<{ key: string; rows: ISpeedDataMarkResponse[]; error: boolean } | null>(null);
  const { fn: loadMarks } = useApiCall(
    (
      api,
      args: { trainId: number; pointIds: string; startTimestamp: string; endTimestamp: string; measureType: number },
    ) =>
      api.fetchJson({ url: "/point-v2/speed/data-marks", method: "GET", data: args }) as Promise<
        ISpeedDataMarkResponse[]
      >,
    { success: false },
  );
  const loadMarksRef = useRef(loadMarks);
  useEffect(() => {
    loadMarksRef.current = loadMarks;
  }, [loadMarks]);

  useEffect(() => {
    if (!selectedPoints.length) return;
    void loadTrainPoints(trainId);
  }, [loadTrainPoints, selectedPoints.length, trainId]);

  useEffect(() => {
    if (!queryRange || !requestKey) return;
    let cancelled = false;
    void loadMarksRef
      .current(undefined, { trainId, pointIds, ...queryRange, measureType: SIGNAL_WAVEFORM_MEASURE_TYPE })
      .then(rows => {
        if (!cancelled) setResult({ key: requestKey, rows: rows ?? [], error: false });
      })
      .catch(() => {
        if (!cancelled) setResult({ key: requestKey, rows: [], error: true });
      });
    return () => {
      cancelled = true;
    };
  }, [pointIds, queryRange, requestKey, trainId]);

  const settled = requestKey !== null && result?.key === requestKey;
  const rows = settled && !result.error ? result.rows : EMPTY_ROWS;
  const isLoading = selectedPoints.length > 0 && requestKey !== null && !settled;
  const controlSamples = trendSeries[0]?.items;
  const marks = useMemo(() => {
    if (!settled || result?.error) return null;

    return pairOrbitSignalMarks({
      points: allPoints.length ? allPoints : selectedPoints,
      selectedPoints,
      rows,
      controlParameter: selectedParameter ? { title: selectedParameter.title, units: controlUnits } : null,
      controlSamples: controlSamples ?? [],
      visibleRange: globalRange,
    }).filter(mark => isDateInRange(mark.date, globalRange));
  }, [
    allPoints,
    controlSamples,
    controlUnits,
    globalRange,
    result?.error,
    rows,
    selectedParameter,
    selectedPoints,
    settled,
  ]);

  useEffect(() => {
    if (!requestKey) {
      setDataMarks([]);
      setDataMarksError(false);
      return;
    }

    if (!settled) {
      setDataMarksError(false);
      return;
    }

    if (result?.error || !marks) {
      setDataMarks([]);
      setDataMarksError(true);
      return;
    }

    setDataMarksError(false);
    setDataMarks(previous => {
      const markSignature = (mark: IMultiAnalysisDataMark) => {
        if (!isOrbitPairMark(mark)) return mark.key;
        const control = mark.orbitPair.controlParameter;
        return [
          mark.key,
          mark.date,
          control?.title ?? "",
          control?.value ?? "",
          control?.units ?? "",
          mark.orbitPair.x.pickupAngle ?? "",
          mark.orbitPair.y.pickupAngle ?? "",
        ].join("|");
      };
      const unchanged =
        previous.length === marks.length &&
        previous.every((mark, index) => markSignature(mark) === markSignature(marks[index]));

      return unchanged ? previous : marks;
    });
  }, [marks, requestKey, result?.error, setDataMarks, setDataMarksError, settled]);

  useEffect(() => {
    onLoadingChange(isLoading);
    return () => onLoadingChange(false);
  }, [isLoading, onLoadingChange]);

  return null;
};
